import { ReactNode, createContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAccessToken } from '~/thunks/auth/authSlice';
import { DEFAULT_TIMEOUT_RECONNECT_WSS, MAX_TIMEOUT_RECONNECT_WSS } from '~/utils/constants/common';
import { wssURL } from '~/utils/constants/env';
import { CODE_CLOSE_WSS_LOGOUT, REASON_CLOSE_WSS_LOGOUT } from '~/utils/constants/websocket';
import { StorageEnum } from '~/utils/enum/base';
import { WebSocketEventEnum } from '~/utils/enum/websocket';
import { deliveredMessage, login } from '~/utils/helper/websocket';
import { IDataWSSSendMessage, IMessageWSS } from '~/utils/interface/websocket';

type ContextValueWS = {
  wss?: WebSocket | null;
  message?: IMessageWSS<IDataWSSSendMessage>;
  socketId?: string;
};

const WebSocketContext = createContext<ContextValueWS>({});

type WebSocketContextProp = {
  children: ReactNode;
};

const WebSocketProvider = ({ children }: WebSocketContextProp) => {
  const labelLog = '[WebSocketProvider]';

  const accessToken = useSelector(selectAccessToken);

  const wssRef = useRef<WebSocket | null>();
  const [message, setWSData] = useState<IMessageWSS<IDataWSSSendMessage>>();
  const [socketId, setSocketId] = useState<string>();
  const timeoutReconnect = useRef<NodeJS.Timeout>();
  const reconnectDelay = useRef<number>(DEFAULT_TIMEOUT_RECONNECT_WSS);
  const contextValueWS: ContextValueWS = { wss: wssRef.current, message, socketId };

  useEffect(() => {
    if (wssRef.current) {
      return;
    }
    const accessTokenPersist = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
    if (accessTokenPersist || accessToken) {
      connectWebSocket();
    }
  }, [accessToken]);

  useEffect(() => {
    return () => {
      timeoutReconnect.current && clearTimeout(timeoutReconnect.current);
    };
  }, []);

  const connectWebSocket = () => {
    const labelLogLocal = `${labelLog} [connectWebSocket]`;
    if (!wssURL) {
      return;
    }

    const resolvedURL =
      window.location.protocol === 'https:'
        ? wssURL.replace(/^ws:\/\//i, 'wss://')
        : wssURL;

    let ws: WebSocket;
    try {
      ws = new WebSocket(resolvedURL);
    } catch {
      console.warn(`${labelLogLocal} WebSocket URL is invalid or blocked by mixed-content policy`);
      return;
    }
    wssRef.current = ws;
    wssRef.current.onopen = () => {
      console.log(`${labelLogLocal} [onopen] webSocket connected`);
      reconnectDelay.current = DEFAULT_TIMEOUT_RECONNECT_WSS; // reset backoff on successful connect
      wssRef.current && login(wssRef.current);
      timeoutReconnect.current && clearTimeout(timeoutReconnect.current); // Clear timeout reconnect
    };

    wssRef.current.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === WebSocketEventEnum.CONNECT_SUCCESS && data.socketId) {
        setSocketId(data.socketId as string);
      }
      if (data.type === WebSocketEventEnum.SEND_MESSAGE) {
        if (wssRef.current) {
          deliveredMessage(wssRef.current, data.data?.conversationId, [Number(data?.data?.id)]);
        }
      }
      setWSData(data);
    };

    wssRef.current.onclose = (e) => {
      const accessTokenPersist = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
      if ((e.code === CODE_CLOSE_WSS_LOGOUT && e.reason === REASON_CLOSE_WSS_LOGOUT) || !accessTokenPersist) {
        wssRef.current = null;
        reconnectDelay.current = DEFAULT_TIMEOUT_RECONNECT_WSS;
        return;
      }
      const delay = reconnectDelay.current;
      reconnectDelay.current = Math.min(delay * 2, MAX_TIMEOUT_RECONNECT_WSS);
      timeoutReconnect.current = setTimeout(() => {
        console.log(`${labelLogLocal} [onclose] webSocket reconnect in ${delay}ms`);
        wssRef.current = null;
        connectWebSocket();
      }, delay);
    };

    wssRef.current.onerror = (err) => {
      ws.close();
    };
  };

  return <WebSocketContext.Provider value={contextValueWS}>{children}</WebSocketContext.Provider>;
};

export { WebSocketContext, WebSocketProvider };
